import JobStatus from 'utils/enums/job-status';
import { ProductionStatus } from 'utils/enums/production';
import { TripStatus } from 'utils/enums/trip-statuses';

// Keep in sync with backend enum
export enum QueueStatus {
	Paused = 'paused',
	Active = 'active',
}

export enum WebSocketsModules {
  Trips = 'Trips',
  Reservations = 'Reservations',
  DriverLocations = 'DriverLocations',
  ElectricVehicle = 'ElectricVehicle',
  DataExport = 'DataExport',
  DataUpload = 'DataUpload',
  Auth = 'Auth',
  Production = 'Production',
  Integrations = 'Integrations',
  Queue = 'Queue',
}

export enum WebSocketsTripEvents {
  New = 'NEW',
  Update = 'UPDATE',
  Cancel = 'CANCEL',
  StatusChange = 'STATUS_CHANGE',
  NewETA = 'NEW_ETA',
}

export enum WebSocketsReservationEvents {
  New = 'NEW',
  Update = 'UPDATE',
  Cancel = 'CANCEL',
  StatusChange = 'STATUS_CHANGE',
}

export enum WebSocketsDriverLocationEvents {
  Update = 'UPDATE',
}

export enum WebSocketsElectricVehicleEvents {
  Update = 'UPDATE',
}

export enum WebSocketsDataUploadEvents {
  Progress = 'PROGRESS',
}

export enum WebSocketsDataExportEvents {
  DataReadyForDownload = 'DATA_READY_FOR_DOWNLOAD',
  DataExportSucceeded = 'DATA_EXPORT_SUCCEEDED',
  DataExportFailed = 'DATA_EXPORT_FAILED',
}

export enum WebSocketsAuthEvents {
  TwoFactorAuthEnforced = 'TWO_FACTOR_AUTH_ENFORCED',
}

export enum WebSocketsProductionEvents {
  Progress = 'PROGRESS',
}

export enum WebSocketsIntegrationsEvents {
  Syncing = 'SYNCING',
}

export enum WebSocketsQueueEvents {
  Status = 'STATUS',
}

export type WebSocketsModulesEvents = {
  [WebSocketsModules.Trips]: WebSocketsTripEvents;
  [WebSocketsModules.Reservations]: WebSocketsReservationEvents;
  [WebSocketsModules.DriverLocations]: WebSocketsDriverLocationEvents;
  [WebSocketsModules.DataUpload]: WebSocketsDataUploadEvents;
  [WebSocketsModules.DataExport]: WebSocketsDataExportEvents;
  [WebSocketsModules.Auth]: WebSocketsAuthEvents;
  [WebSocketsModules.Production]: WebSocketsProductionEvents;
  [WebSocketsModules.ElectricVehicle]: WebSocketsElectricVehicleEvents;
  [WebSocketsModules.Integrations]: WebSocketsIntegrationsEvents;
  [WebSocketsModules.Queue]: WebSocketsQueueEvents;
};

export type WebSocketsModulesEventsData = {
  [WebSocketsModules.Trips]: {
    [WebSocketsTripEvents.New]: {
      ids: number[];
    };
    [WebSocketsTripEvents.Update]: {
      ids: number[];
    };
    [WebSocketsTripEvents.Cancel]: {
      ids: number[];
    };
    [WebSocketsTripEvents.StatusChange]: {
      ids: number[];
      newStatus: TripStatus;
    };
    [WebSocketsTripEvents.NewETA]: {
      id: number;
      eta: number[];
    };
  };
  [WebSocketsModules.Reservations]: {
    [WebSocketsReservationEvents.New]: {
      ids: number[];
    };
    [WebSocketsReservationEvents.Update]: {
      ids: number[];
    };
    [WebSocketsReservationEvents.Cancel]: {
      ids: number[];
    };
    [WebSocketsReservationEvents.StatusChange]: {
      ids: number[];
      newStatus: JobStatus;
    };
  };
  [WebSocketsModules.DriverLocations]: {
    [WebSocketsDriverLocationEvents.Update]: {
      driverId: number;
      vehicleId?: number;
      coordinates: [number, number];
      driverName: string;
    };
  };
  [WebSocketsModules.ElectricVehicle]: {
    [WebSocketsElectricVehicleEvents.Update]: {
      vehicleId: number;
      vehicleName: string;
      evState: IElectricVehicleState;
    };
  };
  [WebSocketsModules.DataUpload]: {
    [WebSocketsDataUploadEvents.Progress]: {
      jobId: number;
      percentage: number;
      completed: boolean;
      invalidRows?: number;
    };
  };
  [WebSocketsModules.DataExport]: {
    [WebSocketsDataExportEvents.DataReadyForDownload]: {
      jobId: string;
      downloadLink: string;
    };
    [WebSocketsDataExportEvents.DataExportSucceeded]: {
      jobId: string;
    };
    [WebSocketsDataExportEvents.DataExportFailed]: {
      jobId: string;
      reason?: string;
    };
  };
  [WebSocketsModules.Auth]: {
    [WebSocketsAuthEvents.TwoFactorAuthEnforced]: never;
  };
  [WebSocketsModules.Production]: {
    [WebSocketsProductionEvents.Progress]: {
      productionId: number;
      productionStatus: ProductionStatus;
      percentComplete?: number;
      errorMessage?: string;
    };
  };
  [WebSocketsModules.Integrations]: {
    [WebSocketsIntegrationsEvents.Syncing]: {
      integrationId: string;
      percentage: number;
      isCompleted?: boolean;
    };
  };
  [WebSocketsModules.Queue]: {
    [WebSocketsQueueEvents.Status]: {
      status: QueueStatus;
    };
  };
};

export interface IElectricVehicleCachedState {
  capacity: number;
  range: number;
  kmPerBattery: number;
  timestamp: number;
}

export type IElectricVehicleState = Omit<IElectricVehicleCachedState, 'timestamp'>;

// If no event data, will return never. If event data exists, will return type of event data declared above in WebSocketsModulesEventsData
export type WebSocketsEventData<Module extends WebSocketsModules, Event extends WebSocketsModulesEvents[Module]> = {
  [EventName in keyof WebSocketsModulesEventsData[Module]]: Event extends EventName ? WebSocketsModulesEventsData[Module][Event] : never;
}[keyof WebSocketsModulesEventsData[Module]];
